import { Inject, Injectable, DOCUMENT } from '@angular/core';
import {
  CONSENTABLE_CATEGORIES,
  CONSENT_COOKIE_NAME,
  CONSENT_EXPIRY_DAYS,
  CONSENT_VERSION,
  IConsentState,
  TConsentableCategory,
  TCookieCategory
} from '@shared/interfaces/cookie-consent.interface';
import { isConsentSuppressedPath } from '@shared/helpers/consent-path.helper';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class CookieConsentService {
  public readonly consent$: BehaviorSubject<IConsentState | null>;
  public readonly bannerVisible$: BehaviorSubject<boolean>;
  public readonly settingsVisible$ = new BehaviorSubject<boolean>(false);

  private isSuppressed: boolean = Boolean(false);

  constructor(@Inject(DOCUMENT) private readonly _document: Document) {
    // Embed suppression must be decided here, at construction, rather than
    // via a later suppress() call from AppComponent: grantedOnce$/consent$
    // subscribers (e.g. FacebookPixelService, wired up through DI) can
    // subscribe before that later call runs, and grantedOnce$ emits
    // synchronously for an already-granted state — so a late suppress()
    // can never retract a value a subscriber already received. Deciding it
    // here, before the subjects are seeded, closes that race.
    const suppressed = isConsentSuppressedPath(
      this._document.location.pathname
    );
    this.isSuppressed = suppressed;

    const persisted = suppressed ? null : this._read();
    this.consent$ = new BehaviorSubject<IConsentState | null>(persisted);
    this.bannerVisible$ = new BehaviorSubject<boolean>(
      suppressed ? false : !persisted
    );
  }

  public isGranted(category: TCookieCategory): boolean {
    if (category === 'necessary') {
      return true;
    }

    return this.consent$.value?.categories[category] === true;
  }

  public grantedOnce$(category: TConsentableCategory): Observable<IConsentState> {
    return this.consent$.pipe(
      filter(
        (state: IConsentState | null) =>
          state?.categories[category] === true
      ),
      take(1),
      map((state: IConsentState | null) => state as IConsentState)
    );
  }

  public acceptAll(): void {
    this._apply({ functional: true, analytics: true, advertisement: true });
  }

  public rejectAll(): void {
    this._apply({ functional: false, analytics: false, advertisement: false });
  }

  public save(categories: Record<TConsentableCategory, boolean>): void {
    this._apply(categories);
  }

  // The auth wipe (cleanStorage on logout/401/expired token) deletes every
  // cookie, but consent is per-browser, not per-account: callers snapshot
  // the persisted decision here and re-write it once the wipe is done.
  public preserve(): () => void {
    const state = this._read();
    if (!state) {
      return () => {};
    }

    return () => this._write(state);
  }

  public openSettings(): void {
    this.settingsVisible$.next(true);
  }

  public closeSettings(): void {
    this.settingsVisible$.next(false);
  }

  // Embed/iframe contexts: hide all consent UI. No consent is ever recorded,
  // so no non-essential scripts load there either.
  public suppress(): void {
    this.isSuppressed = true;
    this.bannerVisible$.next(false);
    this.settingsVisible$.next(false);
  }

  private _apply(categories: Record<TConsentableCategory, boolean>): void {
    // No consent UI exists in an embed, so nothing is ever recordable there.
    if (this.isSuppressed) {
      return;
    }

    const previous = this.consent$.value;
    const revoked = CONSENTABLE_CATEGORIES.some(
      (c: TConsentableCategory) =>
        previous?.categories[c] === true && categories[c] === false
    );

    const state: IConsentState = {
      version: CONSENT_VERSION,
      updatedAt: new Date().toISOString(),
      categories
    };

    this._write(state);
    this.consent$.next(state);
    this.bannerVisible$.next(false);
    this.settingsVisible$.next(false);

    // Scripts already injected can't be un-run; a reload is the only clean
    // way to honor a withdrawal.
    if (revoked) {
      this._reload();
    }
  }

  private _read(): IConsentState | null {
    const raw = this._document.cookie
      .split('; ')
      .find((c: string) => c.startsWith(`${CONSENT_COOKIE_NAME}=`));

    if (!raw) {
      return null;
    }

    try {
      const state: IConsentState = JSON.parse(
        decodeURIComponent(raw.substring(CONSENT_COOKIE_NAME.length + 1))
      );

      const hasValidCategories =
        !!state.categories &&
        CONSENTABLE_CATEGORIES.every(
          (c: TConsentableCategory) => typeof state.categories[c] === 'boolean'
        );

      if (state.version !== CONSENT_VERSION || !hasValidCategories) {
        return null;
      }

      return state;
    } catch {
      return null;
    }
  }

  private _write(state: IConsentState): void {
    const value = encodeURIComponent(JSON.stringify(state));
    const maxAge = CONSENT_EXPIRY_DAYS * 24 * 60 * 60;
    const base = `${CONSENT_COOKIE_NAME}=${value}; path=/; max-age=${maxAge}`;

    // Inside another site's frame (the legacy Bullhorn custom tab loads
    // /iframe cross-site) a SameSite=Lax cookie is neither stored nor sent, so
    // the banner would come back on every load. A partitioned cookie (CHIPS)
    // is keyed by the embedding site, so the choice made inside Bullhorn is
    // remembered there, independently of the one made on the site itself. It
    // must be SameSite=None and Secure, and is kept host-only: the domain
    // probe below cannot work from a cross-site frame anyway.
    if (this._isCrossOriginFrame()) {
      this._document.cookie = `${base}; SameSite=None; Secure; Partitioned`;
      return;
    }

    const domain = this._cookieDomain();
    const secure =
      this._document.location.protocol === 'https:' ? '; Secure' : '';
    const domainAttr = domain ? `; domain=${domain}` : '';

    this._document.cookie = `${base}; SameSite=Lax${domainAttr}${secure}`;
  }

  // True when the page is framed by a different origin (same-origin frames,
  // Karma's test runner included, can read the Lax cookie and need nothing
  // special). Reading a cross-origin top's location throws.
  private _isCrossOriginFrame(): boolean {
    const win = this._document.defaultView;
    if (!win || !win.top || win.self === win.top) {
      return false;
    }

    try {
      return typeof win.top.location.hostname !== 'string';
    } catch {
      return true;
    }
  }

  // Widest domain the browser accepts (e.g. .vitay.io from
  // references.vitay.io) so consent is shared across the app subdomains.
  // Public suffixes (co.uk, com.au, ...) are rejected by the browser, which
  // is exactly what makes the probe safe.
  private _cookieDomain(): string {
    const host = this._document.location.hostname;
    if (host === 'localhost' || /^[\d.]+$/.test(host)) {
      return '';
    }

    const parts = host.split('.');
    for (let i = Math.max(parts.length - 2, 0); i >= 0; i--) {
      const candidate = `.${parts.slice(i).join('.')}`;
      this._document.cookie = `vitay_cc_probe=1; path=/; domain=${candidate}; max-age=5`;
      if (this._document.cookie.includes('vitay_cc_probe=1')) {
        this._document.cookie = `vitay_cc_probe=1; path=/; domain=${candidate}; max-age=0`;
        return candidate;
      }
    }

    return '';
  }

  private _reload(): void {
    this._document.location.reload();
  }
}
